import type { ReactNode } from "react";
import { SectionHeader } from "./section-header";

type Props = {
  title: string;
  description?: string;
  /** Primary action (e.g. a <FormSubmit>). */
  action?: ReactNode;
  children: ReactNode;
};

/**
 * Settings "edit" card — title/description, body fields, and a primary action.
 * Pure LAYOUT: it renders the card grid, not a `<form>`. Wrap it in a
 * `<Form schema={…}>` (src/forms) so validation lives on the schema and the
 * form element is owned by one place. (Previously this owned its own `<form>`;
 * that nested two forms once callers moved to `<Form>`.)
 *
 * Layout adapts to the card's own width (container query, not the viewport, so
 * it's correct even when the sidebar rail + settings nav eat horizontal space):
 *   - wide (@md+): action sits top-right, beside the header; fields below.
 *   - narrow:      title → description → fields → full-width action footer.
 *
 * This keeps the action out of the awkward gap between the description and the
 * first field on phones (it reads top→bottom like a form should).
 */
export function CardForm({ title, description, action, children }: Props) {
  return (
    <div className="card @container grid grid-cols-1 gap-4 @md:grid-cols-[1fr_auto]">
      <div className="@md:col-start-1 @md:row-start-1">
        <SectionHeader title={title} description={description} />
      </div>
      <div className="@md:col-start-1 @md:col-span-2 @md:row-start-2">{children}</div>
      {action && (
        <div className="@md:col-start-2 @md:row-start-1 @md:justify-self-end [&>*]:w-full @md:[&>*]:w-auto">
          {action}
        </div>
      )}
    </div>
  );
}
